import { Fragment } from "react";

/**
 * AsyncListShell
 *
 * Root-cause fix for the repeated
 *   `NotFoundError: Failed to execute 'removeChild' on 'Node':
 *    The node to be removed is not a child of this node.`
 * that fired identically across all 4 admin views (Inventario, Consignantes,
 * Compradores, Socios).
 *
 * The bug pattern (copy-pasted in every admin page) was:
 *
 *   <tbody>                                // or a grid <div>
 *     {loading && <tr>Cargando…</tr>}      // unkeyed placeholder
 *     {!loading && empty && <tr>Vacío</tr>} // unkeyed placeholder
 *     {rows.map(r => <tr key={r.id}>…</tr>)} // keyed dynamic list
 *   </tbody>
 *
 * Rendering unkeyed conditional siblings in the SAME parent as a keyed dynamic
 * list makes React reuse the first placeholder's fiber slot for the first keyed
 * row when `loading` flips false. The commit phase then tries to remove the
 * placeholder DOM node from the parent whose child list has already been
 * reordered by the reconciler — hence removeChild fails. External DOM
 * wrappers (translation extensions, visual-edit inspectors that add
 * `[data-debug-wrapper]` around interactive nodes) make it deterministic.
 *
 * The shell renders placeholders as EXCLUSIVE siblings of the data container.
 * `loading`, `empty` and `content` never share a parent — only one is mounted
 * at a time. Consumers pass the fully-composed table/grid as `children`.
 *
 * Every branch is wrapped in a keyed <Fragment> so React never tries to
 * reconcile the placeholder fiber with the content fiber across the state
 * transition.
 */
export function AsyncListShell({
  loading,
  isEmpty,
  loadingLabel = "Cargando…",
  emptyLabel = "Sin resultados.",
  loadingTestId,
  emptyTestId,
  className = "",
  children,
}) {
  if (loading) {
    return (
      <Fragment key="state-loading">
        <div
          className={`rounded-sm border border-[#3E5641]/10 bg-white px-4 py-10 text-center text-sm text-[#6B7678] ${className}`}
          data-testid={loadingTestId}
        >
          {loadingLabel}
        </div>
      </Fragment>
    );
  }
  if (isEmpty) {
    return (
      <Fragment key="state-empty">
        <div
          className={`rounded-sm border border-[#3E5641]/10 bg-white px-4 py-10 text-center text-sm text-[#6B7678] ${className}`}
          data-testid={emptyTestId}
        >
          {emptyLabel}
        </div>
      </Fragment>
    );
  }
  return <Fragment key="state-content">{children}</Fragment>;
}
